﻿import { DateTime } from 'luxon';
import * as yup from 'yup';

import { checkValidZipCode } from './helper';
import { LTLQuotingState } from './types';

type yupThisObject = {
  createError: (error: { message: string }) => void;
  parent: LTLQuotingState;
};

const isWeightEmpty = (value: unknown) =>
  value === null || value === undefined || value === '';

// 0 is not an entered dimension; `is-positive` flags it instead.
const isDimensionProvided = (value: unknown): boolean => {
  if (isWeightEmpty(value)) return false;
  const numeric = Number(value);
  return !isNaN(numeric) && numeric > 0;
};

/**
 * Dimensions are all-or-none: either all three (L/W/H) are provided or none
 * are. A partially-filled set flags the missing field(s) as required.
 */
const isAnyDimensionProvided = (parent: unknown): boolean => {
  if (typeof parent !== 'object' || parent === null) return false;
  const item = parent as {
    lengthInch?: unknown;
    widthInch?: unknown;
    heightInch?: unknown;
  };
  return [item.lengthInch, item.widthInch, item.heightInch].some(
    isDimensionProvided,
  );
};

const dimensionField = (label: string) =>
  yup
    .mixed()
    .nullable()
    .test('is-number-or-empty', `${label} must be a number`, (value) =>
      isWeightEmpty(value) ? true : !isNaN(Number(value)),
    )
    .test('is-positive', `${label} must be greater than 0`, (value) => {
      if (isWeightEmpty(value) || isNaN(Number(value))) return true;
      return Number(value) > 0;
    })
    .test(
      'all-or-none-dimensions',
      `${label} is required when other dimensions are entered`,
      function (value) {
        if (!isWeightEmpty(value)) return true;
        return !isAnyDimensionProvided(this.parent);
      },
    );

const LoadItemBreakdownSchema = yup.object().shape({
  weight: yup
    .mixed()
    .test('is-number-or-empty', 'weight must be a number', (value) => {
      return value === '' || !isNaN(Number(value)) || value === undefined;
    })
    .test('is-positive', 'Weight must be greater than 0', (value) => {
      if (isWeightEmpty(value)) return true;
      return Number(value) > 0;
    })
    .nullable()
    .test(
      'must-be-at-most-six-digits',
      'Weight must be at most 6 digits',
      (value) => {
        if (isWeightEmpty(value)) return true;
        return Number(value) <= 999999;
      },
    )
    .required('Weight is required'),
  class: yup
    .mixed()
    .nullable()
    .test('class-required', 'Class is required', (value) => {
      return value !== null && value !== undefined && String(value) !== '';
    }),
});

const LoadItemSchema = yup.object().shape({
  quantity: yup
    .number()
    .required('Quantity is required')
    .test('is-decimal', 'Quantity cannot be a decimal', (value) => {
      return Boolean(value) && !value.toString().includes('.');
    })
    .moreThan(0, 'Quantity must be greater than 0'),
  item: yup.string().required('Unit type is required'),
  description: yup
    .string()
    .nullable()
    .max(150, 'Description must be at most 150 characters'),
  pieces: yup.mixed().nullable(),
  weight: yup
    .mixed()
    .test('is-number-or-empty', 'weight must be a number', (value) => {
      return value === '' || !isNaN(Number(value)) || value === undefined;
    })
    .test('is-positive', 'Weight must be greater than 0', (value) => {
      return Number(value) > 0;
    })
    .nullable()
    .test(
      'must-be-at-least-999',
      'Weight must be at most 6 digits',
      (value) => {
        return Number(value) <= 999999;
      },
    )
    .required('Weight is required'),
  lengthInch: dimensionField('Length'),
  widthInch: dimensionField('Width'),
  heightInch: dimensionField('Height'),
  nmfc: yup.string().nullable(),
  class: yup.string().required('Class is required'),
  hazmat: yup.boolean().nullable(),
  loadItemHazmatDetail: yup
    .mixed()
    .nullable()
    .test(
      'hazmat-detail-required',
      'Hazmat details are incomplete',
      function (value) {
        const { hazmat } = this.parent as { hazmat?: boolean };
        if (!hazmat) return true;

        const detail = value as Record<
          string,
          string | null | undefined
        > | null;

        const unNumber = detail?.unNumber?.trim().toUpperCase() ?? '';
        if (!detail || !unNumber) {
          return this.createError({
            message: 'Hazmat: UN/NA Number is required',
          });
        }
        if (!/^(UN|NA)\d{4}$/.test(unNumber)) {
          return this.createError({
            message:
              'Hazmat: UN/NA Number must be "UN" or "NA" followed by 4 digits',
          });
        }
        if (!detail.contactNumber?.trim()) {
          return this.createError({
            message: 'Hazmat: Emergency Contact Number is required',
          });
        }
        if (detail.contactNumber.replace(/\D/g, '').length < 10) {
          return this.createError({
            message:
              'Hazmat: Emergency Contact Number must be at least 10 digits',
          });
        }
        if (!detail.contactName?.trim()) {
          return this.createError({
            message: 'Hazmat: Emergency Contact Name is required',
          });
        }
        return true;
      },
    ),
  uom: yup.string().nullable(),
  quoteLoadItemBreakdown: yup.array().of(LoadItemBreakdownSchema),
});

export const LTLQuotingSchema = yup.object().shape({
  originZip: yup
    .string()
    .required()
    .test({
      name: 'originZip',
      test: function (value) {
        const { createError, parent }: yupThisObject = this;

        if (!value)
          return createError({
            message: 'Origin Zip Code is required',
          });

        if (!checkValidZipCode(value)) {
          return createError({
            message: 'Invalid Origin Zip Code',
          });
        }

        if (!parent.originCity) {
          return createError({
            message:
              parent.originZipHasResults === false
                ? 'Zip code not available'
                : 'Please make a selection',
          });
        }
        return true;
      },
    }),
  destinationZip: yup
    .string()
    .required()
    .test({
      name: 'destinationZip',
      test: function (value) {
        const { createError, parent }: yupThisObject = this;
        if (!value)
          return createError({
            message: 'Destination Zip Code is required',
          });
        if (!checkValidZipCode(value)) {
          return createError({
            message: 'Invalid Destination Zip Code',
          });
        }
        if (!parent.destinationCity) {
          return createError({
            message:
              parent.destinationZipHasResults === false
                ? 'Zip code not available'
                : 'Please make a selection',
          });
        }
        return true;
      },
    }),
  pickupDate: yup
    .date()
    .typeError('Please provide the Expected Ship Date.')
    .test(
      'is-current-or-future-date',
      'Invalid Expected Ship Date. Date cannot be before today.',
      (value) => {
        if (!value) return false;
        const today = DateTime.now().startOf('day');
        const inputDate = DateTime.fromJSDate(value).startOf('day');
        return inputDate >= today;
      },
    )
    .required('Please provide the Expected Ship Date.'),
  declaredValue: yup
    .number()
    .max(999999, 'Must be less than 999999')
    .nullable(),
  loadItems: yup.array().of(LoadItemSchema),
});

export const TestLaneSchema = yup.object().shape({
  originZip: yup
    .string()
    .required()
    .test({
      name: 'originZip',
      test: function (value) {
        const { createError, parent }: yupThisObject = this;

        if (!value)
          return createError({
            message: 'Origin Zip Code is required',
          });

        if (!checkValidZipCode(value)) {
          return createError({
            message: 'Invalid Origin Zip Code',
          });
        }

        if (!parent.originCity) {
          return createError({
            message: 'Please make a selection',
          });
        }
        return true;
      },
    }),
  destinationZip: yup
    .string()
    .required()
    .test({
      name: 'destinationZip',
      test: function (value) {
        const { createError, parent }: yupThisObject = this;
        if (!value)
          return createError({
            message: 'Destination Zip Code is required',
          });
        if (!checkValidZipCode(value)) {
          return createError({
            message: 'Invalid Destination Zip Code',
          });
        }
        if (!parent.destinationCity) {
          return createError({
            message: 'Please make a selection',
          });
        }
        return true;
      },
    }),
  quantity: yup
    .number()
    .required('Quantity is required')
    .test('is-decimal', 'Quantity cannot be a decimal', (value) => {
      return Boolean(value) && !value.toString().includes('.');
    })
    .moreThan(0, 'Quantity must be greater than 0'),
  weight: yup
    .mixed()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .test('is-number-or-empty', 'weight must be a number', (value) => {
      return value === '' || !isNaN(Number(value)) || value === undefined;
    })
    .test('is-positive', 'Weight must be greater than 0', (value) => {
      return Number(value) > 0;
    })
    .nullable()
    .test(
      'must-be-at-least-999',
      'Weight must be at most 6 digits',
      (value) => {
        return Number(value) <= 999999;
      },
    )
    .required('Weight is required'),
  lengthInch: yup
    .number()
    .nullable()
    .moreThan(-1, 'Length cannot be negative')
    .required('Length is required'),
  widthInch: yup
    .number()
    .nullable()
    .moreThan(-1, 'Width cannot be negative')
    .required('Width is required'),
  heightInch: yup
    .number()
    .nullable()
    .moreThan(-1, 'Height cannot be negative')
    .required('Height is required'),
  class: yup.string().required('Class is required'),
  unitType: yup.string().required('Unit type is required'),
});
